import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Loader2, Plus, Search, Sparkles, X } from "lucide-react";
import { api } from "../../lib/api.js";

export interface ClusterOption {
  id: string;
  nameHe: string | null;
  nameEn: string;
}

/**
 * Searchable cluster dropdown.
 *
 * A native <select> over 600+ clusters could not be searched, sorted English
 * names only, and offered no way out when the right cluster did not exist yet —
 * so keywords stayed unassigned. This one searches Hebrew name, English name and
 * id, can create a cluster in place, and can hand the decision to the classifier.
 */
export function ClusterPicker({
  value,
  clusters,
  onChange,
  onAiAssign,
  disabled,
  busy,
  compact = true,
  placeholder = "(no cluster)",
}: {
  value: string | null;
  clusters: ClusterOption[];
  onChange: (clusterId: string | null) => void;
  /** Omit to hide the AI option. */
  onAiAssign?: () => void;
  disabled?: boolean;
  /** Shows a spinner on the trigger (e.g. while the AI call runs). */
  busy?: boolean;
  compact?: boolean;
  placeholder?: string;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [nameHe, setNameHe] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [createErr, setCreateErr] = useState<string | null>(null);
  const [createBusy, setCreateBusy] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const current = value ? clusters.find((c) => c.id === value) : undefined;
  const label = current ? current.nameHe || current.nameEn : value ?? placeholder;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function close() {
    setOpen(false);
    setQuery("");
    setCreating(false);
    setCreateErr(null);
  }

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? clusters.filter((c) =>
          (c.nameHe ?? "").toLowerCase().includes(q) || c.nameEn.toLowerCase().includes(q) || c.id.toLowerCase().includes(q))
      : clusters;
    return list.slice(0, 100);
  }, [clusters, query]);
  const totalMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q
      ? clusters.filter((c) =>
          (c.nameHe ?? "").toLowerCase().includes(q) || c.nameEn.toLowerCase().includes(q) || c.id.toLowerCase().includes(q)).length
      : clusters.length;
  }, [clusters, query]);

  function pick(id: string | null) {
    close();
    if (id !== value) onChange(id);
  }

  function startCreate() {
    const q = query.trim();
    const isHebrew = /[֐-׿]/.test(q);
    setNameHe(isHebrew ? q : "");
    setNameEn(isHebrew ? "" : q);
    setCreateErr(null);
    setCreating(true);
  }

  async function submitCreate() {
    if (!nameHe.trim() || !nameEn.trim()) {
      setCreateErr("Both a Hebrew and an English name are needed (the English one becomes the id).");
      return;
    }
    setCreateBusy(true);
    setCreateErr(null);
    try {
      const r = await api.createCluster({ nameHe: nameHe.trim(), nameEn: nameEn.trim() });
      await qc.invalidateQueries({ queryKey: ["pipeline", "clusters"] });
      void qc.invalidateQueries({ queryKey: ["research-clusters"] });
      pick(r.cluster.id);
    } catch (e) {
      setCreateErr((e as Error).message);
    } finally {
      setCreateBusy(false);
    }
  }

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        disabled={disabled}
        onClick={() => (open ? close() : setOpen(true))}
        title={current ? `${current.nameHe ?? ""} · ${current.nameEn} (${current.id})` : "Assign a cluster"}
        className={`inline-flex items-center gap-1 rounded border bg-white px-1.5 text-left hover:border-muted disabled:opacity-60 ${
          compact ? "h-6 max-w-[220px] text-2xs" : "h-8 max-w-[280px] text-xs"
        } ${value ? "border-line text-muted-dark" : "border-warn/50 text-amber-700"}`}
      >
        {busy ? <Loader2 className="h-3 w-3 shrink-0 animate-spin" /> : null}
        <span className="truncate" dir="auto">{label}</span>
        <ChevronDown className="h-3 w-3 shrink-0 opacity-60" />
      </button>

      {open && (
        <div className="absolute left-0 z-30 mt-1 w-80 rounded-lg border border-line bg-white text-xs shadow-lg">
          {!creating ? (
            <>
              <div className="flex items-center gap-1.5 border-b border-line px-2 py-1.5">
                <Search className="h-3.5 w-3.5 shrink-0 text-muted" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && matches[0]) pick(matches[0].id); }}
                  placeholder="Search clusters — Hebrew, English or id"
                  dir="auto"
                  className="w-full border-0 bg-transparent p-0 text-xs focus:outline-none focus:ring-0"
                />
                {query && (
                  <button type="button" onClick={() => setQuery("")} className="text-muted hover:text-ink">
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>

              <div className="border-b border-line py-1">
                {onAiAssign && (
                  <button
                    type="button"
                    onClick={() => { close(); onAiAssign(); }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left font-medium text-brand-700 hover:bg-brand-50"
                  >
                    <Sparkles className="h-3.5 w-3.5" /> Assign with AI
                  </button>
                )}
                <button
                  type="button"
                  onClick={startCreate}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-ink hover:bg-canvas"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {query.trim() ? <>Create new cluster “<span dir="auto">{query.trim()}</span>”</> : "Create new cluster"}
                </button>
              </div>

              <ul className="max-h-72 overflow-y-auto py-1">
                {value && (
                  <li>
                    <button type="button" onClick={() => pick(null)} className="w-full px-3 py-1.5 text-left text-muted hover:bg-canvas">
                      (clear cluster)
                    </button>
                  </li>
                )}
                {matches.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => pick(c.id)}
                      className={`flex w-full flex-col px-3 py-1.5 text-left hover:bg-canvas ${c.id === value ? "bg-brand-50" : ""}`}
                    >
                      <span className="truncate text-ink" dir="auto">{c.nameHe || c.nameEn}</span>
                      <span className="truncate text-2xs text-muted">{c.nameEn} · {c.id}</span>
                    </button>
                  </li>
                ))}
                {matches.length === 0 && (
                  <li className="px-3 py-2 text-muted">No cluster matches — create one above, or let the AI decide.</li>
                )}
                {totalMatches > matches.length && (
                  <li className="px-3 py-1.5 text-2xs text-muted">
                    Showing {matches.length} of {totalMatches} — keep typing to narrow.
                  </li>
                )}
              </ul>
            </>
          ) : (
            <div className="space-y-2 p-3">
              <div className="font-medium text-ink">New cluster</div>
              <label className="block">
                <span className="text-2xs text-muted">Hebrew name</span>
                <input value={nameHe} onChange={(e) => setNameHe(e.target.value)} dir="rtl" className="input h-7 text-xs" autoFocus />
              </label>
              <label className="block">
                <span className="text-2xs text-muted">English name (becomes the id)</span>
                <input
                  value={nameEn}
                  onChange={(e) => setNameEn(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") void submitCreate(); }}
                  className="input h-7 text-xs"
                />
              </label>
              {createErr && <div className="text-2xs text-danger">{createErr}</div>}
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => void submitCreate()} disabled={createBusy} className="btn-primary btn-sm">
                  {createBusy ? "Creating…" : "Create and assign"}
                </button>
                <button type="button" onClick={() => setCreating(false)} className="btn-ghost btn-sm">Back</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
